import ColorPickerV2 from 'src/components/common/ColorPickerV2'
import RichTextEditor from 'src/components/common/form-elements/RichTextEditor'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useEffect, useState } from 'react'
import { DotsSixVertical, Info, Minus, Plus, PlusCircle, Trash } from '@phosphor-icons/react'
import {
  Icon,
  IconButton,
  LinkButton,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@shopflo/ui'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import type { BannerItemConfigType } from 'src/lib/types/customizeUI'

const ANIMATION_STYLE_VALUES = ['vertical_ticker', 'horizontal_marquee'] as const

type AnimationStyleValue = typeof ANIMATION_STYLE_VALUES[number]

const stripHtml = (html: string | undefined) => (html ?? '').replace(/<[^>]*>/g, '').trim()
const isBannerTextEmpty = (text: string | undefined) => !stripHtml(text)

export const TopBannerControl = () => {
  const { t } = useLocale()
  const {
    getters: { cartBannerUIConfigData, showBannerErrors },
    actions: { setCartBannerConfig }
  } = useCartContext()

  const enableMultipleBanners = cartBannerUIConfigData.multipleBannersEnabled ?? false
  const banners: BannerItemConfigType[] = cartBannerUIConfigData.banners ?? []
  const defaultBgColor = cartBannerUIConfigData.color || '#000000'
  const defaultTextColor = cartBannerUIConfigData.textColor || '#FFFFFF'

  const [selectedBannerId, setSelectedBannerId] = useState<string | null>(banners[0]?.id ?? null)
  const [focusBannerId, setFocusBannerId] = useState<string | null>(null)
  const animationStyle = (cartBannerUIConfigData.animationStyle ?? 'vertical_ticker') as AnimationStyleValue
  const animationDelay = cartBannerUIConfigData.animationDelay ?? 1

  useEffect(() => {
    if (!enableMultipleBanners || banners.length > 0) return
    const firstBanner = {
      id: `${Date.now()}`,
      position: 0,
      color: defaultBgColor,
      textColor: defaultTextColor,
      text: cartBannerUIConfigData.text ?? ''
    }
    setCartBannerConfig({ ...cartBannerUIConfigData, banners: [firstBanner] })
    setSelectedBannerId(firstBanner.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableMultipleBanners])

  useEffect(() => {
    if (!enableMultipleBanners || banners.length === 0) return
    const stillExists = selectedBannerId && banners.some(b => b.id === selectedBannerId)
    if (!stillExists) setSelectedBannerId(banners[0].id)
  }, [banners, enableMultipleBanners, selectedBannerId])
  // Add this useEffect after the existing ones
  useEffect(() => {
    // Clear focus when banners change to prevent stale focus
    if (focusBannerId && !banners.some(b => b.id === focusBannerId)) {
      setFocusBannerId(null)
    }
  }, [banners, focusBannerId])

  const setBanners = (nextBanners: typeof banners) => {
    const first = nextBanners[0]
    setCartBannerConfig({
      ...cartBannerUIConfigData,
      multipleBannersEnabled: true,
      banners: nextBanners,
      ...(first
        ? {
            color: first.color,
            textColor: first.textColor,
            text: first.text
          }
        : {})
    })
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    if (result.destination.index === result.source.index) return

    const items = Array.from(banners)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    setBanners(items.map((b, i) => ({ ...b, position: i })))
  }

  return (
    <div className='space-y-5'>
      <div className='grid w-full grid-cols-2 gap-2'>
        <ColorPickerV2
          label='Background'
          color={defaultBgColor}
          onChange={color => {
            const nextConfig = {
              ...cartBannerUIConfigData,
              color
            }

            nextConfig.banners = (banners || []).map(b => ({ ...b, color }))

            setCartBannerConfig(nextConfig)
          }}
        />
        <ColorPickerV2
          label='Text'
          color={defaultTextColor}
          onChange={color => {
            const nextConfig = {
              ...cartBannerUIConfigData,
              textColor: color
            }
            nextConfig.banners = (banners || []).map(b => ({ ...b, textColor: color }))

            setCartBannerConfig(nextConfig)
          }}
        />
      </div>

      <div className='flex items-center justify-between grid w-full grid-cols-2 gap-2'>
        <div className='text-left font-inter text-sm font-medium leading-[22px] tracking-[-0.14px] text-[var(--Text-subtext-darker,#52525C)]'>
          {t('banner_type_label')}
        </div>
        <SegmentedControl
          variant='bright'
          className='h-8 w-full'
          value={enableMultipleBanners}
          onChange={(val: any) => {
            const nextConfig = {
              ...cartBannerUIConfigData,
              multipleBannersEnabled: val
            }

            if (!val) {
              const firstBanner = banners[0]
              if (firstBanner) {
                nextConfig.color = firstBanner.color
                nextConfig.textColor = firstBanner.textColor
                nextConfig.text = firstBanner.text
              }
            } else {
              if (banners.length === 0) {
                const firstBanner = {
                  id: `${Date.now()}`,
                  position: 0,
                  color: nextConfig.color || defaultBgColor,
                  textColor: nextConfig.textColor || defaultTextColor,
                  text: nextConfig.text || ''
                }
                nextConfig.banners = [firstBanner]
              }
            }

            setCartBannerConfig(nextConfig)
            if (val && (!selectedBannerId || !banners.some(b => b.id === selectedBannerId))) {
              setSelectedBannerId(banners[0]?.id ?? null)
            }
          }}
          items={[
            { value: false, label: t('banner_type_single') },
            { value: true, label: t('banner_type_multiple') }
          ]}
        />
      </div>

      <div className='space-y-4'>
        {enableMultipleBanners ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='top-banner-list'>
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps} className='space-y-3'>
                  {banners.slice(0, 5).map((banner, index) => {
                    const isSelected = banner.id === selectedBannerId
                    const isLastBanner = banners.length === 1
                    return (
                      <Draggable key={banner.id} draggableId={banner.id} index={index}>
                        {draggableProvided => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            className='flex w-full gap-1'
                          >
                            <div className='flex flex-row items-center gap-1 pt-2.5 self-start'>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span tabIndex={-1} className='inline-flex'>
                                    <IconButton
                                      size='xs'
                                      variant='ghost'
                                      IconComponent={Trash}
                                      iconProps={{ weight: 'regular' }}
                                      disabled={isLastBanner}
                                      onClick={e => {
                                        e.stopPropagation()
                                        const next = banners
                                          .filter(b => b.id !== banner.id)
                                          .map((b, i) => ({ ...b, position: i }))
                                        const wasSelected = banner.id === selectedBannerId

                                        setBanners(next)

                                        if (wasSelected && next.length > 0) {
                                          const nextIndex = Math.min(index, next.length - 1)
                                          setSelectedBannerId(next[nextIndex]?.id ?? null)
                                          setTimeout(() => setFocusBannerId(next[nextIndex]?.id ?? null), 0)
                                        } else if (wasSelected) {
                                          setSelectedBannerId(null)
                                          setFocusBannerId(null)
                                        } else if (focusBannerId === banner.id) {
                                          setFocusBannerId(null)
                                        }
                                      }}
                                      aria-label='Delete banner'
                                    />
                                  </span>
                                </TooltipTrigger>
                                {isLastBanner && (
                                  <TooltipContent side='bottom' className='max-w-[200px] p-3'>
                                    {t('banner_multiple_min_required_error')}
                                  </TooltipContent>
                                )}
                              </Tooltip>
                              <IconButton
                                {...draggableProvided.dragHandleProps}
                                size='xs'
                                variant='ghost'
                                IconComponent={DotsSixVertical}
                                iconProps={{ weight: 'bold' }}
                                aria-label='Reorder banner'
                                disabled={isLastBanner}
                              />
                            </div>

                            <div
                              className='flex-1 min-w-0 cursor-pointer'
                              onClick={() => {
                                setSelectedBannerId(banner.id)
                                setFocusBannerId(banner.id)
                              }}
                            >
                              <div className='flex-1 min-w-0 transition-all duration-[250ms] ease-in-out'>
                                <RichTextEditor
                                  charLimit={60}
                                  value={banner.text}
                                  handleTextChange={(content: string) => {
                                    if (!isSelected) return
                                    const next = banners.map(b => (b.id === banner.id ? { ...b, text: content } : b))
                                    setBanners(next)
                                  }}
                                  placeholder={t('banner_text_placeholder')}
                                  groupName={banner.id}
                                  initialDataLoaded={true}
                                  showToolbar={isSelected}
                                  forceAutoFocus={banner.id === focusBannerId ? 'end' : false}
                                />
                              </div>
                              {showBannerErrors && isBannerTextEmpty(banner.text) && (
                                <p className='mt-1 text-text-xs-regular text-red-600'>{t('banner_text_empty_error')}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : null}

        {!enableMultipleBanners ? (
          <div className='space-y-1'>
            <RichTextEditor
              charLimit={60}
              value={cartBannerUIConfigData.text}
              handleTextChange={(content: string) => {
                const nextConfig = {
                  ...cartBannerUIConfigData,
                  text: content
                }
                nextConfig.banners = (banners || []).map((b, i) => (i === 0 ? { ...b, text: content } : b))
                setCartBannerConfig(nextConfig)
              }}
              placeholder={t('banner_text_placeholder')}
            />
          </div>
        ) : null}

        {enableMultipleBanners ? (
          <div>
            <div className='flex items-center gap-1'>
              <LinkButton
                leftIcon={<Icon IconComponent={PlusCircle} className='text-zinc-600 justify-start' size='xs' />}
                size='xs'
                variant='ghost'
                className={banners.length >= 5 ? 'opacity-40 pointer-events-none' : ''}
                onClick={() => {
                  if (banners.length >= 5) return
                  const next = [
                    ...banners,
                    {
                      id: `${Date.now()}-${banners.length}`,
                      position: banners.length,
                      color: defaultBgColor,
                      textColor: defaultTextColor,
                      text: ''
                    }
                  ]
                  setBanners(next)
                  setSelectedBannerId(next[next.length - 1].id)
                }}
              >
                {t('banner_add')}
              </LinkButton>
              <span className='text-text-xs-medium text-zinc-400'>({Math.min(banners.length, 5)}/5)</span>
            </div>
            <div className='mt-3 h-px w-full bg-zinc-950/[0.06]' />
            <div className='mt-3 grid w-full grid-cols-2 items-center gap-2'>
              <div className='text-left font-inter text-sm font-medium leading-[22px] tracking-[-0.14px] text-[var(--Text-subtext-darker,#52525C)]'>
                {t('banner_animation_style')}
              </div>
              <Select
                value={animationStyle}
                onValueChange={value => setCartBannerConfig({ ...cartBannerUIConfigData, animationStyle: value })}
              >
                <SelectTrigger className='w-full h-8 bg-white'>
                  <div className='text-text-sm-regular'>{t(`banner_animation_style_${animationStyle}`)}</div>
                </SelectTrigger>
                <SelectContent>
                  {ANIMATION_STYLE_VALUES.map(value => (
                    <SelectItem key={value} value={value}>
                      {t(`banner_animation_style_${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='mt-3 flex items-center justify-between gap-2'>
              <div className='flex shrink-0 items-center gap-1 text-left font-inter text-sm font-medium leading-[22px] tracking-[-0.14px] text-[var(--Text-subtext-darker,#52525C)]'>
                {t('banner_animation_delay')}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='flex cursor-default items-center text-zinc-400'>
                      <Info size={14} weight='regular' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side='left' className='max-w-[200px]'>
                    {t('banner_animation_delay_tooltip')}
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className='flex items-center gap-1.5 rounded-lg bg-white py-1.5 px-1.5 shadow-sm ring-1 ring-black/10'>
                <IconButton
                  size='xs'
                  variant='ghost'
                  IconComponent={Minus}
                  disabled={animationDelay <= 1}
                  onClick={() =>
                    setCartBannerConfig({ ...cartBannerUIConfigData, animationDelay: Math.max(1, animationDelay - 1) })
                  }
                  aria-label='Decrease delay'
                />
                <div className='flex items-center'>
                  <input
                    type='number'
                    min={1}
                    max={10}
                    step={1}
                    value={animationDelay}
                    onChange={e => {
                      const val = Math.min(10, Math.max(1, Number(e.target.value)))
                      setCartBannerConfig({ ...cartBannerUIConfigData, animationDelay: val })
                    }}
                    className='w-5 bg-transparent text-center text-sm text-zinc-700 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
                  />
                  <span className='text-sm text-zinc-400'>s</span>
                </div>
                <IconButton
                  size='xs'
                  variant='ghost'
                  IconComponent={Plus}
                  disabled={animationDelay >= 10}
                  onClick={() =>
                    setCartBannerConfig({ ...cartBannerUIConfigData, animationDelay: Math.min(10, animationDelay + 1) })
                  }
                  aria-label='Increase delay'
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
